import { useQuery } from '@tanstack/react-query';

import type { CatalogRetentionConcept } from '@/shared/services/invoice.service';
import { receiverCanWithhold } from '../../lib/retentions';
import type { DraftIssuer, DraftReceiver, RetentionRule } from '../lib/draft';
import { previewRetentionsService } from '../services/invoicing.service';

const ratesOf = (concept?: CatalogRetentionConcept) => {
  const lines = concept?.retentions ?? [];
  const isrRate = lines.find((line) => line.tax_type === '001')?.rate ?? 0;
  const ivaRate = lines.find((line) => line.tax_type === '002')?.rate ?? 0;
  return isrRate > 0 || ivaRate > 0 ? { isrRate, ivaRate } : null;
};

/**
 * La regla de retención del régimen del emisor frente al receptor, del mismo
 * catálogo revisado que usa el wizard legacy (`/invoices/retentions/preview`,
 * que el fiscal-engine porta con paridad probada contra el NestJS).
 *
 * Una sola consulta por pareja emisor-receptor con dos conceptos de muestra,
 * servicio y venta de bienes: la regla depende del régimen, la actividad y la
 * tasa, nunca del monto. Solo retienen las personas morales, así que con una
 * persona física o un RFC genérico ni se consulta.
 *
 * `isResolving` alimenta el candado del timbrado: timbrar antes de que llegue
 * la regla emitiría a una moral sin las retenciones que le tocan.
 */
export const useRetentionRule = (issuer: DraftIssuer | null, receiver: DraftReceiver | null) => {
  const regime = issuer?.taxRegime ?? '';
  const receiverRfc = receiver?.rfc ?? '';
  const applies = Boolean(regime) && receiverCanWithhold(receiverRfc);

  const query = useQuery({
    queryKey: ['invoicing', 'retention-rule', regime, issuer?.rfc, receiverRfc],
    queryFn: async (): Promise<RetentionRule> => {
      const result = await previewRetentionsService(
        { tax_regime: regime, rfc: issuer?.rfc },
        { rfc: receiverRfc },
        [
          { subtotal: 100, has_transferred_iva: true, is_goods: false },
          { subtotal: 100, has_transferred_iva: true, is_goods: true },
        ],
      );
      return { service: ratesOf(result.concepts?.[0]), goods: ratesOf(result.concepts?.[1]) };
    },
    enabled: applies,
    staleTime: Infinity,
  });

  return {
    rule: applies ? (query.data ?? null) : null,
    isResolving: applies && query.isLoading,
    failed: applies && query.isError,
  };
};
